import React, { useEffect, useMemo, useState } from "react";
import { staffApi } from "@/lib/staffApi";
import { ErrorState, Skeleton } from "@/components/BookingShell";
import { formatPrice, formatMoney, toISODate } from "@/lib/format";

export function ReportsSummary() {
    const [end, setEnd] = useState(() => toISODate(new Date()));
    const [start, setStart] = useState(() => toISODate(new Date(Date.now() - 30 * 86400000)));
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [services, setServices] = useState(null);

    const load = () => {
        setError(null); setData(null);
        Promise.all([staffApi.reports(start, end), staffApi.services()])
            .then(([r, s]) => { setData(r); setServices(s); })
            .catch(setError);
    };
    useEffect(load, [start, end]);

    const svcName = useMemo(() => {
        const m = {};
        (services?.services || []).forEach((s) => { m[s.id] = s.name; });
        return m;
    }, [services]);

    return (
        <div className="space-y-6" data-testid="reports-summary">
            <div>
                <h1 className="font-serif font-light text-4xl text-navy">Reports</h1>
                <p className="text-sm text-navy/60 mt-1">
                    Live financial figures. <span className="text-gold font-medium">Booked</span> revenue counts all non-cancelled appointments;
                    <span className="text-gold font-medium"> Paid</span> revenue counts payments marked as paid.
                </p>
            </div>

            <div className="rounded-2xl glass p-4 flex flex-wrap items-center gap-3" data-testid="report-range">
                <label className="text-xs text-white/80">
                    From
                    <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" className="ml-2 bg-white/10 border border-white/25 rounded-md px-2 py-1 text-white text-xs [color-scheme:dark]" />
                </label>
                <label className="text-xs text-white/80">
                    To
                    <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" className="ml-2 bg-white/10 border border-white/25 rounded-md px-2 py-1 text-white text-xs [color-scheme:dark]" />
                </label>
            </div>

            {error && <ErrorState error={error} onRetry={load} />}
            {!data && !error && <Skeleton className="h-64" />}

            {data && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-kpis">
                        <KPI label="Booked (range)" value={formatMoney(data.totals.rangeRevenueBooked)} tone="gold" testId="kpi-booked" />
                        <KPI label="Paid (range)" value={formatMoney(data.totals.rangeRevenuePaid)} testId="kpi-paid" />
                        <KPI label="Appointments" value={data.totals.rangeAppointments} testId="kpi-appts" />
                        <KPI label="Avg value" value={formatMoney(data.totals.averageValue)} testId="kpi-avg" />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <section className="rounded-2xl glass p-5" data-testid="status-breakdown">
                            <h2 className="font-serif text-xl text-white mb-3">Status breakdown</h2>
                            <ul className="text-sm text-white/80 space-y-2">
                                {Object.entries(data.statusCounts).map(([k, v]) => (
                                    <li key={k} className="flex justify-between border-b border-white/5 pb-1">
                                        <span className="capitalize">{k}</span>
                                        <span className="text-gold">{v}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>

                        <section className="rounded-2xl glass p-5" data-testid="top-services">
                            <h2 className="font-serif text-xl text-white mb-3">Most-booked services</h2>
                            {data.topServices.length === 0 ? (
                                <div className="text-sm text-white/50">No services yet.</div>
                            ) : (
                                <ul className="text-sm text-white/80 space-y-2">
                                    {data.topServices.map((s) => (
                                        <li key={s.serviceId} className="flex justify-between border-b border-white/5 pb-1">
                                            <span>{svcName[s.serviceId] || `Service #${s.serviceId}`} <span className="text-white/40">({s.count})</span></span>
                                            <span className="text-gold">{formatMoney(s.revenue)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>

                    <p className="text-[11px] text-navy/50" data-testid="report-disclaimer">
                        New/returning customer split, no-show rate and outstanding-balance breakdowns require deeper joins across
                        payments and customer records and are shown per-customer on their profile pages. This roll-up gives the
                        live financial figures for the selected range.
                    </p>
                </>
            )}
        </div>
    );
}

function KPI({ label, value, tone, testId }) {
    return (
        <div className={`rounded-2xl glass p-4 min-w-0 ${tone === "gold" ? "ring-1 ring-gold/40" : ""}`} data-testid={testId}>
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/60 break-words">{label}</div>
            <div className={`mt-2 font-serif text-2xl break-words ${tone === "gold" ? "gold-text" : "text-white"}`}>{value}</div>
        </div>
    );
}
